import React, { useState } from 'react';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { generateSeoProps } from 'router/seoprops/generateSeoProps';

import { useQuery } from 'react-query';
import { userSkillOverview, competencyMarketRadar } from 'services/competency';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Spinner from '@core/components/spinner/Spinner';
import ShadowBox from '@core/components/shadowbox/ShadowBox';
import useWindowSize from 'hooks/use-windowSize';
import LinkButton from '@core/components/linkbutton/LinkButton';
import CompetencyTabPanel from 'components/pages/career-competency/competencytabpanel/CompetencyTabPanel';
import CompetencyFilters from 'components/pages/career-competency/competencyfilters/CompetencyFilters';

import classes from './career-competency.module.scss';

const index = () => {
    const seoProps = generateSeoProps({
        title: 'Career Competency: Elevate Your Pharma Career with Targeted Skills - Pharmuni',
        description: 'Identify and develop key competencies for a successful pharma career. Explore targeted skills, personalized learning paths, and expert guidance to elevate your professional journey.',
        canonical: 'career-competency',
    });
    const { mobileDevice } = useWindowSize();
    const router = useRouter();
    const [filters, setFilters] = useState({ countryCode: undefined, seniority: undefined, window: 'last_30d' });
    const marketRadarQuery = (() => {
        const params = new URLSearchParams({ window: filters.window });
        if (filters.countryCode) params.set('country_code', filters.countryCode);
        if (filters.seniority) params.set('seniority', filters.seniority);
        return `?${params.toString()}`;
    })();

    const { data, isLoading } = useQuery('user-skill-overview', userSkillOverview.GET, {
        onError: (error) => {
            if (error?.response?.status === 428) {
                router.push('/verification');
            }
        }
    });
    const { data: marketRadarData, isLoading: marketRadarLoading } = useQuery(
        ['competency-market-radar', marketRadarQuery],
        () => competencyMarketRadar.GET(marketRadarQuery),
        { keepPreviousData: true }
    );
    const [tabValue, setTabValue] = useState('1');

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
    };
    return (
        <>
            <NextSeo {...seoProps} />
            {data && !isLoading && marketRadarData && !marketRadarLoading ? (
                <div className={classes['wrapper']}>
                    <ShadowBox>
                        <Box sx={{ width: '100%', typography: 'body1' }}>
                            <TabContext value={tabValue}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                                    <TabList
                                        className={classes['tab-list']}
                                        onChange={handleChange}
                                        variant="scrollable"
                                        sx={{
                                            justifyContent: 'flex-start',
                                            borderBottom: 'none',
                                            '.MuiTabs-indicator': { display: 'none' },
                                        }}
                                    >
                                        <Tab
                                            label="Your full picture"
                                            value="1"
                                        />
                                        <Tab
                                            label="Skills from learning"
                                            value="2"
                                        />
                                        <Tab
                                            label="What your CV shows"
                                            value="3"
                                        />
                                    </TabList>
                                </Box>
                                <div className={classes['filters-section']}>
                                    <CompetencyFilters
                                        mobileDevice={mobileDevice}
                                        onChange={setFilters}
                                    />
                                </div>
                                <CompetencyTabPanel
                                    value="1"
                                    chartData={data?.data.combined_scores}
                                    emptyChartData={data?.data.course_boost}
                                    marketData={marketRadarData?.data.market_scores}
                                    title="Your Competency Overview"
                                    description="Your competency overview combines insights from your skill tree, course boost, and resume analysis to provide a holistic view of your strengths and areas for growth. As you engage with courses and activities, these insights will evolve, guiding you on your career development journey."
                                    emptyTitle="Your radar is waiting to be filled"
                                    emptyDescription="Upload your CV or complete your first course and watch this shape grow. The larger the radar, the stronger your profile against today's pharma job market."
                                    infoItems={[
                                        { title: 'Your skill shape', description: 'Across 8 pharma competency areas' },
                                        { title: 'Market comparison', description: 'vs live job demand in your target country' },
                                        { title: 'Gap score', description: 'Which skills to grow to unlock more jobs' }
                                    ]}
                                    emptyActions={
                                        <>
                                            <LinkButton to="/explore?featured_contents=true&content=lecture" kind="primary-color">Explore Courses</LinkButton>
                                            <LinkButton to="/profile/my-resume" kind="secondary-lecture">Import Resume</LinkButton>
                                        </>
                                    }
                                />
                                <CompetencyTabPanel
                                    value="2"
                                    chartData={data?.data.course_boost}
                                    emptyChartData={data?.data.course_boost}
                                    marketData={marketRadarData?.data.market_scores}
                                    title="Your Course Boost Insights"
                                    description="This radar only counts the skills you've built through Pharmuni — courses completed, assessments passed, and certificates earned. It starts small and grows as you learn. Compare it to your CV view to see which skills you're actively building versus which ones you're relying on from past experience"
                                    emptyTitle="This grows as you learn"
                                    emptyDescription="Start your first course and this radar begins to fill. Every module you complete, every assessment you pass adds to your shape — and widens your gap over your CV alone."
                                    infoItems={[
                                        { title: 'Courses completed', description: 'Each finished course grows a skill axis' },
                                        { title: 'Assessments passed', description: 'Validated scores carry extra weight' },
                                        { title: 'Certificates earned', description: 'Boosts your full picture radar too' }
                                    ]}
                                    emptyActions={
                                        <>
                                            <LinkButton to="/explore?featured_contents=true&content=lecture" kind="primary-color">Explore Courses</LinkButton>
                                            <LinkButton to="/career-path" kind="secondary-lecture">Explore Career Paths</LinkButton>
                                        </>
                                    }
                                />
                                <CompetencyTabPanel
                                    value="3"
                                    chartData={data?.data.skill_insight}
                                    emptyChartData={data?.data.course_boost}
                                    marketData={marketRadarData?.data.market_scores}
                                    title="Your Resume Insights"
                                    description="This radar is drawn entirely from your uploaded CV — the skills, roles, and experience you've built so far. If a skill area looks small here, it means employers reading your CV wouldn't see strong evidence of it yet. Updating your CV or adding a new role will reshape this view."
                                    emptyTitle="No CV uploaded yet"
                                    emptyDescription="This view reads your CV and maps your experience across 8 pharma competency areas. Upload your CV and we'll show you how employers see your skills right now."
                                    infoItems={[
                                        { title: 'What employers see', description: 'How your past experience maps to pharma competencies' },
                                        { title: 'CV blind spots', description: 'Skills missing from your CV that jobs demand' },
                                        { title: 'Instant update', description: 'Re-upload anytime — your radar reshapes automatically' }
                                    ]}
                                    emptyActions={
                                        <LinkButton to="/profile/my-resume" kind="secondary-lecture">Import Resume</LinkButton>
                                    }
                                />
                            </TabContext>
                        </Box>
                    </ShadowBox>
                </div>
            ) : (
                <div className={classes['loading']}>
                    <Spinner />
                </div>
            )
            }
        </>
    );
};

export default index;